import { PageLoader } from '@customerio/pluma-components/react';
import SignupPage from '@/pages/signup/index.page';
import type { SignupPageProps } from '@/pages/signup/types';
import { useSignupPublicFlags } from '@/services/signup/public-flags';
import { Navigate } from '@/utils/navigate';
import { LegacyRedirectReporter } from './-legacy-redirects';

/**
 * Serves the signup funnel. Holds render until the signup public flags settle
 * so the first paint classifies the run correctly — `classifySignupFlow` reads
 * dark mode and the skip-onboarding sequence off the flags, so rendering while
 * they are unresolved would flash the wrong variant. The store resolves once
 * per load (a failed fetch settles all-off), so the decision can't flip after
 * the first paint.
 */
export function SignupRouteGate(props: SignupPageProps) {
  const publicFlags = useSignupPublicFlags();

  if (publicFlags == null) {
    return <PageLoader />;
  }

  return <SignupPage {...props} />;
}

/**
 * Redirects the legacy `/users/register` and `/users/signup` aliases into the
 * funnel, preserving query params (promo codes, marketing attribution) the
 * legacy link carried. Reported to Sentry so legacy-URL traffic stays
 * observable while these aliases wind down. A plain Navigate is fine here:
 * /signup is Hydra-owned.
 */
export function LegacySignupRedirect() {
  const search = Object.fromEntries(new URLSearchParams(window.location.search));
  return (
    <LegacyRedirectReporter>
      <Navigate to="/signup" replace={true} search={search} />
    </LegacyRedirectReporter>
  );
}

/**
 * Serves `/signup/<unknown-segment>` URLs (retired steps like `plan-choice`,
 * stale links): replace-redirect to the entry step rather than silently
 * rendering it under the stale URL.
 */
export function SignupUnknownStepRedirect() {
  const search = Object.fromEntries(new URLSearchParams(window.location.search));
  return <Navigate to="/signup" replace={true} search={search} />;
}
